import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Phone, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { COLLECTIONS, OFFICIAL_LOGO } from "@/lib/collections";

export const LOGO_URL = OFFICIAL_LOGO;

// Official KGD logo (uploaded by client — transparent-friendly background)
export function KgdLogo({ className = "h-12", label = false, invert = false }) {
    return (
        <span className={`inline-flex items-center ${className}`}>
            <img
                src={OFFICIAL_LOGO}
                alt="Ménage KGD — KGD S.E.N.C."
                className="h-full w-auto object-contain"
                style={{ filter: invert ? "drop-shadow(0 2px 14px rgba(212,175,55,0.35))" : "drop-shadow(0 2px 12px rgba(0,0,0,0.35))" }}
                loading="eager"
                decoding="async"
            />
        </span>
    );
}

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [collOpen, setCollOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => setOpen(false), [loc.pathname]);
    // Prevent scroll on scroll flag (kept for future use)
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    // Prevent body scroll + hide mobile bar when menu is open
    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden";
            document.body.dataset.menuOpen = "true";
        } else {
            document.body.style.overflow = "";
            delete document.body.dataset.menuOpen;
        }
        return () => { document.body.style.overflow = ""; delete document.body.dataset.menuOpen; };
    }, [open]);

    const linkClass = ({ isActive }) =>
        `link-underline text-sm tracking-wide transition-colors whitespace-nowrap ${isActive ? "text-kgd-gold" : "text-white/90 hover:text-white"}`;

    return (
        <>
            <header
                data-testid="site-header"
                className="fixed top-0 inset-x-0 z-50 transition-all duration-500 bg-[#020713]/95 shadow-[0_2px_24px_rgba(0,0,0,0.4)] backdrop-blur-md border-b border-kgd-gold/25"
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[76px] lg:h-[92px]">
                    <Link to="/" data-testid="nav-logo" className="flex items-center">
                        <KgdLogo className="h-14 lg:h-16"/>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-4 xl:gap-6 2xl:gap-8">
                        <NavLink to="/" end className={linkClass} data-testid="nav-home">Accueil</NavLink>
                        <div className="relative" onMouseEnter={() => setCollOpen(true)} onMouseLeave={() => setCollOpen(false)}>
                            <button data-testid="nav-collections-toggle" className="link-underline text-sm tracking-wide text-white/90 hover:text-white flex items-center gap-1 whitespace-nowrap">
                                Collections <ChevronDown className="w-4 h-4"/>
                            </button>
                            {collOpen && (
                                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-4">
                                    <div className="glass-card rounded-xl p-2 w-72 bg-[#041126]/97">
                                        <Link to="/collections" className="block px-4 py-2 text-sm text-white/90 hover:text-kgd-gold" data-testid="nav-collections-all">Toutes les collections</Link>
                                        <div className="gold-hairline my-1 opacity-40"/>
                                        {COLLECTIONS.map(c => (
                                            <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`nav-coll-${c.slug}`} className="flex items-center justify-between px-4 py-2 text-sm text-white/85 hover:bg-white/5 rounded-md transition-colors">
                                                <span className="flex items-center gap-3">
                                                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.primary, boxShadow: `0 0 6px ${c.primary}` }}/>
                                                    <span>Collection {c.name}</span>
                                                </span>
                                                <ChevronRight className="w-3.5 h-3.5 text-kgd-gold/60"/>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <NavLink to="/a-propos" className={linkClass} data-testid="nav-about">À propos</NavLink>
                        <NavLink to="/zones" className={linkClass} data-testid="nav-zones">Zones desservies</NavLink>
                        <NavLink to="/journal" className={linkClass} data-testid="nav-journal">Journal</NavLink>
                        <NavLink to="/devenir-collectionneur" className={linkClass} data-testid="nav-devenir">Devenir Collectionneur</NavLink>
                        <NavLink to="/contact" className={linkClass} data-testid="nav-contact">Contact</NavLink>
                    </nav>

                    <div className="hidden lg:block">
                        <Link to="/soumission" className="btn-kgd text-sm whitespace-nowrap" data-testid="header-cta-soumission">
                            <Sparkles className="w-4 h-4"/> Évaluation gratuite
                        </Link>
                    </div>

                    <button onClick={() => setOpen(v => !v)} data-testid="mobile-menu-toggle" aria-label="Menu" aria-expanded={open} className="lg:hidden p-2 -mr-2 text-white">
                        {open ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
                    </button>
                </div>
            </header>

            {/* Fullscreen mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden fixed inset-0 z-[60] bg-[#03102A] text-white overflow-y-auto"
                    style={{ height: "100dvh" }}
                >
                    <div className="flex items-center justify-between px-4 sm:px-6 h-[76px] border-b border-kgd-gold/20 bg-[#020713]">
                        <Link to="/" onClick={() => setOpen(false)}><KgdLogo className="h-14"/></Link>
                        <button onClick={() => setOpen(false)} aria-label="Fermer" data-testid="mobile-menu-close" className="p-2 -mr-2"><X className="w-6 h-6"/></button>
                    </div>
                    <div className="px-6 py-8 flex flex-col gap-1 pb-32">
                        <NavLink to="/" end onClick={() => setOpen(false)} className="text-white text-2xl font-serif py-3" data-testid="m-nav-home">Accueil</NavLink>
                        <div className="text-kgd-gold uppercase tracking-[0.28em] text-[10px] pt-6 pb-2">Collections</div>
                        <Link to="/collections" onClick={() => setOpen(false)} className="text-white/90 py-2" data-testid="m-nav-collections">Toutes les collections</Link>
                        {COLLECTIONS.map(c => (
                            <Link key={c.slug} to={`/collections/${c.slug}`} onClick={() => setOpen(false)} data-testid={`m-nav-coll-${c.slug}`} className="flex items-center gap-3 text-white/90 py-2.5">
                                <span className="w-2 h-2 rounded-full" style={{ background: c.primary, boxShadow: `0 0 8px ${c.primary}` }}/> Collection {c.name}
                            </Link>
                        ))}
                        <div className="gold-hairline my-4 opacity-40"/>
                        <NavLink to="/a-propos" onClick={() => setOpen(false)} className="text-white/90 py-2 text-lg font-serif" data-testid="m-nav-about">À propos</NavLink>
                        <NavLink to="/zones" onClick={() => setOpen(false)} className="text-white/90 py-2 text-lg font-serif" data-testid="m-nav-zones">Zones desservies</NavLink>
                        <NavLink to="/journal" onClick={() => setOpen(false)} className="text-white/90 py-2 text-lg font-serif" data-testid="m-nav-journal">Journal</NavLink>
                        <NavLink to="/devenir-collectionneur" onClick={() => setOpen(false)} className="text-white/90 py-2 text-lg font-serif" data-testid="m-nav-devenir">Devenir Collectionneur</NavLink>
                        <NavLink to="/contact" onClick={() => setOpen(false)} className="text-white/90 py-2 text-lg font-serif" data-testid="m-nav-contact">Contact</NavLink>
                        <Link to="/soumission" onClick={() => setOpen(false)} className="btn-kgd mt-8 w-full" data-testid="m-header-cta-soumission">
                            <Sparkles className="w-4 h-4"/> Demander une évaluation gratuite
                        </Link>
                        <a href="tel:+18195815999" className="btn-ghost-gold w-full mt-3" data-testid="m-header-call"><Phone className="w-4 h-4"/> 819-581-5999</a>
                    </div>
                </div>
            )}

            {/* Spacer */}
            <div aria-hidden className="h-[76px] lg:h-[92px]"/>
        </>
    );
}

export function MobileActionBar() {
    // Hidden automatically when the mobile menu is open (body[data-menu-open])
    return (
        <div
            data-testid="mobile-action-bar"
            className="mobile-bar lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#020713]/97 backdrop-blur-md border-t border-kgd-gold/25"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
            <div className="flex items-stretch gap-2 px-3 py-2.5">
                <a href="tel:+18195815999" data-testid="mobile-call" className="flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-white text-[13px] tracking-wide border border-white/12 bg-white/[0.04]">
                    <Phone className="w-4 h-4 text-kgd-gold"/> Appeler
                </a>
                <Link
                    to="/soumission"
                    data-testid="mobile-quote"
                    className="flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 text-white text-[13px] font-medium tracking-wide border border-white/20 shadow-[0_6px_18px_-8px_rgba(0,140,255,0.5),0_4px_16px_-10px_rgba(57,214,0,0.4)]"
                    style={{ background: "linear-gradient(90deg,#0083E8 0%,#00A8E8 50%,#33C700 100%)" }}
                >
                    <Sparkles className="w-4 h-4"/> Évaluation gratuite
                </Link>
            </div>
        </div>
    );
}

export function Footer() {
    return (
        <footer className="bg-[#03102A] text-white/80 border-t border-kgd-gold/20 pb-[calc(120px+env(safe-area-inset-bottom))] lg:pb-8">
            <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
                <div>
                    <KgdLogo className="h-16"/>
                    <p className="mt-5 text-sm text-white/70 leading-relaxed">
                        Ménage KGD — KGD S.E.N.C. Entretien ménager résidentiel et commercial en Mauricie.
                    </p>
                    <div className="mt-4">
                        <div className="inline-flex items-center gap-2 font-serif text-[10px] tracking-[0.28em] uppercase" style={{ color: "#E8D48B" }}>
                            <span>Rigueur</span>
                            <span className="opacity-60">◆</span>
                            <span>Constance</span>
                            <span className="opacity-60">◆</span>
                            <span>Tranquillité d'esprit</span>
                        </div>
                    </div>
                    <p className="mt-4 text-sm italic text-kgd-gold/90 font-serif">« Un espace propre apaise l'esprit. »</p>
                </div>
                <div>
                    <div className="eyebrow mb-4">Collections</div>
                    <ul className="space-y-2 text-sm">
                        {COLLECTIONS.map(c => (
                            <li key={c.slug}><Link to={`/collections/${c.slug}`} className="hover:text-kgd-gold transition-colors" data-testid={`footer-coll-${c.slug}`}>Collection {c.name}</Link></li>
                        ))}
                    </ul>
                </div>
                <div>
                    <div className="eyebrow mb-4">Navigation</div>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/a-propos" className="hover:text-kgd-gold transition-colors">À propos</Link></li>
                        <li><Link to="/zones" className="hover:text-kgd-gold transition-colors">Zones desservies</Link></li>
                        <li><Link to="/journal" className="hover:text-kgd-gold transition-colors">Journal</Link></li>
                        <li><Link to="/soumission" className="hover:text-kgd-gold transition-colors">Évaluation gratuite</Link></li>
                        <li><Link to="/contact" className="hover:text-kgd-gold transition-colors">Contact</Link></li>
                        <li><Link to="/confidentialite" className="hover:text-kgd-gold transition-colors">Politique de confidentialité</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="eyebrow mb-4">Contact</div>
                    <ul className="space-y-2 text-sm">
                        <li><a href="tel:+18195815999" className="hover:text-kgd-gold transition-colors" data-testid="footer-phone">819-581-5999</a></li>
                        <li><a href="mailto:info@menagekgd.ca" className="hover:text-kgd-gold transition-colors" data-testid="footer-email">info@menagekgd.ca</a></li>
                        <li><a href="https://menagekgd.ca" className="hover:text-kgd-gold transition-colors" target="_blank" rel="noreferrer" data-testid="footer-site">menagekgd.ca</a></li>
                        <li className="text-white/60">Trois-Rivières · Shawinigan · Bécancour · Nicolet</li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10 py-6 text-xs text-white/50 text-center px-5">
                © {new Date().getFullYear()} KGD S.E.N.C. — Tous droits réservés. « De catastrophique à éclatant, avec constance et rigueur. »
            </div>
        </footer>
    );
}
